import type { AnyRouter } from "@tanstack/react-router";

import { useAuth0 } from "@auth0/auth0-react";
import { authExchange } from "@urql/exchange-auth";
import { cacheExchange } from "@urql/exchange-graphcache";
import { createClient as createWSClient } from "graphql-ws";
import { jwtDecode } from "jwt-decode";
import { toast } from "sonner";
import {
  Provider,
  fetchExchange,
  createClient,
  subscriptionExchange,
} from "urql";

import { config } from "@/config";
import { currentUserUpdatesResolver } from "@/features/account/components/current-user-updates-resolver";
import { deleteUserNotifications } from "@/features/account/components/MutationUpdates/deleteUserNotifications";
import { checkinUpdatesResolver } from "@/features/check-in/components/check-in-updates-resolver";
import { guardmeUpdatesResolver } from "@/features/guardme/components/guardme-updates-resolver";
import { incidentsUpdatesResolver } from "@/features/incidents/components/incidents-updates-resolver";
import { incidentUpdatesResolver } from "@/features/incidents/components/IncidentUpdates";
import { approveMembershipRequest } from "@/features/members/components/MutationUpdates/approveMembershipRequest";
import { createInvitation } from "@/features/members/components/MutationUpdates/createInvitation";
import { deleteInvitation } from "@/features/members/components/MutationUpdates/deleteInvitation";
import { deleteMember } from "@/features/members/components/MutationUpdates/deleteMember";
import { denyMembershipRequest } from "@/features/members/components/MutationUpdates/denyMembershipRequest";
import { organizationUpdatesResolver } from "@/features/settings/subscriptions/OrganizationUpdates";

type GraphQLProviderProps = Readonly<{
  children: React.ReactNode;
  router: AnyRouter;
}>;

export function GraphqlProvider({ children, router }: GraphQLProviderProps) {
  const { isAuthenticated, getAccessTokenSilently, logout, user } = useAuth0();

  const wsClient = createWSClient({
    connectionParams: async () => {
      if (__APP_STAGE__ === "local" && user?.sub) {
        return { authorization: `Bearer ${user.sub}` };
      }

      try {
        const authToken = await getAccessTokenSilently();
        return {
          authorization: `Bearer ${authToken}`,
        };
      } catch (error) {
        console.error({ error });
        return {};
      }
    },
    url: config.GRAPHQL_WS_ENDPOINT,
  });

  const exchanges = isAuthenticated
    ? [
        cacheExchange({
          // schema: schema,
          keys: {
            SystemPayload: () => null,
            GeographyPoint: () => null,
            GeographyPolygon: () => null,
            UserNotificationConnection: () => null,
          },
          updates: {
            Mutation: {
              approveMembershipRequest,
              createInvitation,
              deleteInvitation,
              deleteMember,
              deleteUserNotifications,
              denyMembershipRequest,
            },
            Subscription: {
              checkInUpdates: checkinUpdatesResolver(router.navigate),
              currentUserUpdates: currentUserUpdatesResolver,
              guardmeUpdates: guardmeUpdatesResolver(router.navigate),
              incidentsUpdates: incidentsUpdatesResolver(router.navigate),
              incidentUpdates: incidentUpdatesResolver,
              organizationUpdates: organizationUpdatesResolver,
            },
          },
        }),
        authExchange(async (utilities) => {
          const accessToken = await getAccessTokenSilently().catch((error) => {
            toast.error("Unable to refresh access token!", {
              description: error,
            });
          });

          const now = new Date();
          // assume token is empty and expires in 5 minutes
          let token = "";
          let expiresAt = now.valueOf() + 300_000;

          if (accessToken) {
            token = accessToken;
            const payload = jwtDecode(accessToken);
            if (payload.exp) {
              expiresAt = payload.exp * 1_000;
            }
          } else {
            toast.warning("Access token not returned from Auth0");
          }
          return {
            addAuthToOperation(operation) {
              if (__APP_STAGE__ === "local" && user?.sub) {
                return utilities.appendHeaders(operation, {
                  Authorization: `Bearer ${user.sub}`,
                });
              }

              return token
                ? utilities.appendHeaders(operation, {
                    Authorization: `Bearer ${token}`,
                  })
                : operation;
            },
            didAuthError(error) {
              if (error.response) {
                return error.response?.status === 401;
              }

              if (error.graphQLErrors && error.graphQLErrors.length > 0) {
                return error.graphQLErrors.some(
                  (item) => item.extensions?.code === "A0001",
                );
              }

              if (error.networkError) {
                return true;
              }
              return false;
            },
            async refreshAuth() {
              try {
                token = await getAccessTokenSilently();
                const payload = jwtDecode(token);
                if (payload.exp) {
                  expiresAt = payload.exp * 1_000;
                }
              } catch {
                logout({
                  clientId: config.AUTH0_CLIENT_ID,
                  logoutParams: {
                    returnTo: config.BASE_URL,
                  },
                });
              }
            },
            willAuthError() {
              return !token || expiresAt < Date.now();
            },
          };
        }),
        fetchExchange,
        subscriptionExchange({
          forwardSubscription: (request) => {
            const input = { ...request, query: request.query || "" };
            return {
              subscribe(sink) {
                const unsubscribe = wsClient.subscribe(input, sink);
                return { unsubscribe };
              },
            };
          },
        }),
      ]
    : [cacheExchange(), fetchExchange];

  const client = createClient({
    preferGetMethod: false,
    url: config.GRAPHQL_HTTP_ENDPOINT,
    exchanges: exchanges,
  });

  return <Provider value={client}>{children}</Provider>;
}
